import React, { useState } from 'react';
import type { SubscriptionPlan } from '../../shared/types';
import { apiService } from '../../shared/services/api';
import BanterLoader from '../../shared/design/components/BanterLoader';
import { LockIcon, SettingsIcon } from './icons/UiIcons';

const PLANS: { plan: SubscriptionPlan; label: string; price: string; features: string[]; color: string }[] = [
    {
        plan: 'STARTER',
        label: 'Starter',
        price: '29 €/mois',
        features: [
            'Programme fidélité (points ou tampons)',
            'Jusqu\'à 200 clients actifs',
            'Analyses de base',
            'Support email',
        ],
        color: 'border-gray-200',
    },
    {
        plan: 'PRO',
        label: 'Pro',
        price: '79 €/mois',
        features: [
            'Tout Starter +',
            'Campagnes push & SMS',
            'Offres flash & promotions ciblées',
            'Clients illimités',
            'Rémi IA (recommandations)',
        ],
        color: 'border-indigo-400 ring-2 ring-indigo-400',
    },
    {
        plan: 'PREMIUM',
        label: 'Premium',
        price: '149 €/mois',
        features: [
            'Tout Pro +',
            'Apple Wallet & Google Wallet',
            'Staff multi-utilisateurs',
            'Rapport détaillé & export',
            'Support prioritaire',
        ],
        color: 'border-purple-400',
    },
];

const BillingManager: React.FC = () => {
    const [loading, setLoading] = useState<SubscriptionPlan | null>(null);
    const [portalLoading, setPortalLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const user = apiService.getCurrentUser();
    const restaurantId = user?.restaurantId;

    const handleSubscribe = async (plan: SubscriptionPlan) => {
        setLoading(plan);
        setError(null);
        try {
            const { url } = await apiService.createCheckoutSession(plan);
            window.location.href = url;
        } catch (e: any) {
            setError(e.response?.data?.message || 'Erreur lors de la création de la session de paiement.');
        } finally {
            setLoading(null);
        }
    };

    const handleManageSubscription = async () => {
        if (!restaurantId) return;
        setPortalLoading(true);
        setError(null);
        try {
            const { url } = await apiService.getBillingPortalUrl(restaurantId);
            window.location.href = url;
        } catch (e: any) {
            setError('Impossible d\'accéder au portail de gestion. Réessayez plus tard.');
        } finally {
            setPortalLoading(false);
        }
    };

    return (
        <div className="space-y-8 animate-fade-in">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="page-title">Abonnement & facturation</h1>
                    <p className="page-subtitle">Choisissez le plan adapté à votre restaurant</p>
                </div>
                <button
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    className="px-5 py-2.5 border border-gray-200 text-gray-700 font-semibold rounded-full hover:bg-gray-50 transition-colors text-sm disabled:opacity-50 flex items-center gap-2"
                >
                    {portalLoading ? (
                        <><BanterLoader size="xs" /> Chargement…</>
                    ) : (
                        <><SettingsIcon className="w-4 h-4" /> Gérer mon abonnement</>
                    )}
                </button>
            </div>

            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium">
                    {error}
                </div>
            )}

            {/* Stripe notice */}
            <div className="p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/30 rounded-xl flex items-start gap-3">
                <span className="text-blue-700 dark:text-blue-300 flex-shrink-0 mt-0.5"><LockIcon className="w-5 h-5" /></span>
                <div>
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">Paiement sécurisé par Stripe</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">Vos données bancaires ne transitent jamais sur nos serveurs. Vous serez redirigé vers la page de paiement Stripe.</p>
                </div>
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {PLANS.map(({ plan, label, price, features, color }) => (
                    <div key={plan} className={`bg-white border-2 rounded-2xl p-6 flex flex-col ${color} ${plan === 'PRO' ? 'shadow-lg shadow-indigo-100' : ''}`}>
                        {plan === 'PRO' && (
                            <div className="inline-flex self-start mb-3 px-3 py-1 bg-indigo-600 text-white text-xs font-bold rounded-full">
                                Recommandé
                            </div>
                        )}
                        <h2 className="text-xl font-bold text-gray-900">{label}</h2>
                        <p className="text-2xl font-extrabold text-gray-900 mt-2 mb-4">{price}</p>
                        <ul className="space-y-2 mb-6 flex-1">
                            {features.map((feat, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                                    <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {feat}
                                </li>
                            ))}
                        </ul>
                        <button
                            onClick={() => handleSubscribe(plan)}
                            disabled={loading !== null}
                            className={`w-full py-3 font-bold rounded-xl transition-colors disabled:opacity-50 text-sm ${
                                plan === 'PRO'
                                    ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md'
                                    : 'bg-gray-900 text-white hover:bg-gray-800'
                            }`}
                        >
                            {loading === plan ? (
                                <span className="flex items-center justify-center gap-2">
                                    <BanterLoader size="xs" />
                                    Redirection…
                                </span>
                            ) : (
                                `Choisir ${label}`
                            )}
                        </button>
                    </div>
                ))}
            </div>

            <p className="text-center text-xs text-gray-400">
                Engagement mensuel, sans frais de résiliation. TVA applicable selon votre pays.
            </p>
        </div>
    );
};

export default BillingManager;
